import { Button, Badge } from '@/components/ui'
import { ExternalLink, Vault, FileText, Sparkles } from 'lucide-react'
import { useGenerateValuationDoc, usePushToVault } from '@/hooks/useFunctions'
import { cn, formatRelativeDate } from '@/lib/utils'
import type { AppRecord } from '@/lib/types'

// Shared button-styled link classes (Button is a native <button>, so we can't
// nest it inside <a> — apply the styling to the anchor itself instead).
const linkBtnBase = 'inline-flex items-center gap-2 rounded-md font-medium transition-all duration-150 tracking-wide font-body px-3 py-1.5 text-xs'
const linkBtnVariants = {
  'gold-outline': 'bg-transparent text-gold-soft border border-gold/60 hover:bg-gold/10',
  quiet: 'bg-transparent text-cream/70 hover:text-cream hover:bg-cream/5 border-transparent',
  ghost: 'bg-transparent text-cream hover:bg-cream/5 border border-cream/20 hover:border-cream/40',
} as const

type Kind = 'valuation' | 'whitepaper' | 'marketing_deck'

const labels: Record<Kind, { kind_title: string; doc_label: string }> = {
  valuation:      { kind_title: 'Valuation',      doc_label: 'Open valuation doc' },
  whitepaper:     { kind_title: 'Whitepaper',     doc_label: 'Open whitepaper'     },
  marketing_deck: { kind_title: 'Marketing Deck', doc_label: 'Open marketing deck' },
}

function getDocUrl(app: AppRecord, kind: Kind): string | undefined {
  if (kind === 'valuation') return app.valuation_doc?.google_doc_url
  if (kind === 'whitepaper') return app.whitepaper?.google_doc_url ?? app.whitepaper?.url
  if (kind === 'marketing_deck') return app.marketing_deck?.gamma_url
  return undefined
}

function getFolderUrl(app: AppRecord, kind: Kind): string | null | undefined {
  if (kind === 'valuation') return app.valuation_doc?.drive_folder_url
  if (kind === 'whitepaper') return app.whitepaper?.drive_folder_url
  if (kind === 'marketing_deck') return app.marketing_deck?.drive_folder_url
  return null
}

/**
 * Reusable bar of Google Doc + Vault actions for any kind of generated artifact.
 * Layout:
 *   [Open in Google Docs] [Open Drive folder] [Push to ARH vault | Open in vault]
 */
export function VaultLinkBar({ app, kind }: { app: AppRecord; kind: Kind }) {
  const { kind_title, doc_label } = labels[kind]
  const docUrl = getDocUrl(app, kind)
  const folderUrl = getFolderUrl(app, kind)
  const vault = app.vault_links?.[kind]

  const generateDoc = useGenerateValuationDoc()
  const push = usePushToVault()

  const isValuation = kind === 'valuation'
  // Only offer the branded-doc generation once a valuation actually exists —
  // there's nothing to render a doc from before then.
  const canGenerateDoc = isValuation && !docUrl && Boolean(app.valuation)

  return (
    <div className="flex items-center gap-2 flex-wrap pt-3 border-t border-bronze/15">
      {docUrl ? (
        <a href={docUrl} target="_blank" rel="noreferrer" className={cn(linkBtnBase, linkBtnVariants['gold-outline'])}>
          <FileText size={12} /> {doc_label}
          <ExternalLink size={10} />
        </a>
      ) : canGenerateDoc ? (
        <Button
          variant="gold-outline"
          size="sm"
          loading={generateDoc.isPending}
          onClick={() => generateDoc.mutate(app.slug)}
        >
          <Sparkles size={12} /> Generate branded Google Doc
        </Button>
      ) : (
        <Badge tone="cream">No Google Doc yet</Badge>
      )}

      {folderUrl && (
        <a href={folderUrl} target="_blank" rel="noreferrer" className={cn(linkBtnBase, linkBtnVariants.quiet)}>
          <ExternalLink size={12} /> Drive folder
        </a>
      )}

      {vault ? (
        <a href={vault.vault_url} target="_blank" rel="noreferrer" className={cn(linkBtnBase, linkBtnVariants.ghost)}>
          <Vault size={12} /> Open in ARH Vault
          <span className="font-mono text-[9px] text-cream/40 ml-1">
            · pushed {formatRelativeDate(vault.pushed_at)}
          </span>
        </a>
      ) : docUrl ? (
        <Button
          variant="ghost"
          size="sm"
          loading={push.isPending}
          onClick={() => push.mutate({ slug: app.slug, kind })}
        >
          <Vault size={12} /> Push {kind_title.toLowerCase()} to ARH Vault
        </Button>
      ) : null}
    </div>
  )
}
